import type { FC, ReactNode } from "react";
import { Button } from "../Button";
import InfoIcon from "../../icons/info.svg?react";
import QuestionBubbleIcon from "../../icons/question_bubble.svg?react";

import styles from "./Suggestions.module.css";
import { AssistantSuggestion } from "../../types/assistant";
import { Sight, SightId } from "../../types";
import { useI18n } from "../../i18n";

type Props = {
  sights: Record<SightId, Sight>;
  suggestions: AssistantSuggestion[];
  handleSuggestion: (suggestion: AssistantSuggestion) => void;
};

/**
 * Inline suggestion list rendered after each assistant message in the chat history.
 */
export const Suggestions: FC<Props> = ({
  sights,
  suggestions,
  handleSuggestion,
}) => {
  const i18n = useI18n();

  const labelForSight = (sightId: SightId) => {
    const sight = sights[sightId];
    if (!sight) {
      return typeof sightId === "string" ? sightId : String(sightId);
    }
    return (
      sight.name ?? (typeof sight.id === "string" ? sight.id : String(sight.id))
    );
  };

  const renderContent = (suggestion: AssistantSuggestion): ReactNode => {
    switch (suggestion.type) {
      case "info":
        return (
          <div className={styles.suggestionContent}>
            <QuestionBubbleIcon data-mirror-rtl />
            <span>{suggestion.label || i18n.assistant.suggestions.info}</span>
          </div>
        );

      case "sight": {
        const sight = sights[suggestion.sightId];
        if (!sight) return null;
        return (
          <div className={styles.suggestionContent}>
            <QuestionBubbleIcon data-mirror-rtl />
            <span>{suggestion.label || labelForSight(suggestion.sightId)}</span>
          </div>
        );
      }

      case "more":
        return (
          <div className={styles.suggestionContent}>
            <QuestionBubbleIcon data-mirror-rtl />
            <span>{suggestion.label || i18n.assistant.suggestions.moreFacts}</span>
          </div>
        );

      case "enter-poi-artifact":
        return (
          <div className={styles.suggestionContent}>
            <InfoIcon />
            <span>{suggestion.label}</span>
          </div>
        );

      default:
        return null;
    }
  };

  // Backend input: a type this client does not draw, or a sight it does not
  // know, is dropped rather than rendered as an empty button.
  const items = suggestions.flatMap((suggestion) => {
    const content = renderContent(suggestion);
    return content ? [{ suggestion, content }] : [];
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.root}>
      <p className={styles.header}>{i18n.assistant.suggestions.header}</p>
      <ul className={styles.list}>
        {items.map(({ suggestion, content }, i) => (
          <li className={styles.item} key={i}>
            <Button
              className={styles.suggestionButton}
              onClick={() => handleSuggestion(suggestion)}
              onPointerDown={(e) => e.preventDefault()}
            >
              {content}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
